import {
  CommentPermission,
  ImagePermission,
  registerGalleryPolicies,
} from '~/modules/gallery/policies';

import { isAdmin } from './helpers';

// Generic permission types
export type Permission = string & {};
export type Module = string & {};
export type Qual = 'any' | 'own' | (string & {});
export type Func = string & {};

export type PermissionKeyFormat<M extends Module = Module> =
  | `${M}.${Permission}`
  | `${M}.${Permission}:${Qual}`
  | `${M}.${Permission}:${Qual}::${Func}`;

export type PermissionKey = '*' | PermissionKeyFormat | (string & {});

// Types for auto completion of known permissions
export type KnownPermissionKey = ImagePermission | CommentPermission | '*';

// Context types
export type Principal = UserState;

export type Ctx<R = unknown> = {
  user?: Principal;
  resource?: R;
  meta?: Record<string, unknown>;
};

type Policy<R = unknown> = (ctx: Ctx<R>) => boolean;

export class RBAC<R = unknown> {
  private allow = new Map<PermissionKey, Policy<R>[]>();
  private deny = new Map<PermissionKey, Policy<R>[]>();

  allowPermission(permission: PermissionKey, policy: Policy<R>) {
    (
      this.allow.get(permission) ??
      this.allow.set(permission, []).get(permission)!
    ).push(policy);
  }

  denyPermission(permission: PermissionKey, policy: Policy<R>) {
    (
      this.deny.get(permission) ??
      this.deny.set(permission, []).get(permission)!
    ).push(policy);
  }

  can(permission: PermissionKey, ctx: Ctx<R>): boolean {
    const keys: PermissionKey[] = this.expandKeys(permission);
    if (this.hit(this.deny, keys, ctx)) return false;
    if (this.hit(this.allow, keys, ctx)) return true;
    return false;
  }

  private parseKey(k: string) {
    const [left, func] = k.split('::'); // e.g. "comment.delete", "hard"?
    const [base, qual] = left.split(':'); // e.g. base="comment.delete", qual="any"?
    return { base, qual, func };
  }

  private collectQualifiersFor(base: string, func?: string) {
    const seen = new Set<string>();

    const visit = (map: Map<PermissionKey, Policy<R>[]>) => {
      for (const key of map.keys()) {
        const s = String(key);
        const { base: b, qual, func: f } = this.parseKey(s);
        if (b !== base) continue;
        // match function presence/value exactly (both undefined or both equal)
        if ((func ?? null) !== (f ?? null)) continue;
        if (qual) seen.add(qual);
      }
    };

    visit(this.allow);
    visit(this.deny);

    // Preferred order: put "any" first, "own" last; rest alphabetical.
    const rank = (q: string) => (q === 'any' ? 0 : q === 'own' ? 2 : 1);
    return Array.from(seen).sort(
      (a, b) => rank(a) - rank(b) || a.localeCompare(b)
    );
  }

  private expandKeys(permission: PermissionKey): PermissionKey[] {
    const requested = String(permission);
    const { base, qual, func } = this.parseKey(requested);

    // 1) Always check the exact key first.
    const keys: PermissionKey[] = [permission];

    // 2) If caller passed a base (no qualifier), discover all qualified variants
    if (!qual) {
      const quals = this.collectQualifiersFor(base, func);
      if (func) {
        for (const q of quals)
          keys.push(`${base}:${q}::${func}` as PermissionKey);
      } else {
        for (const q of quals) keys.push(`${base}:${q}` as PermissionKey);
      }
    }

    // 3) Wildcard last
    keys.push('*');

    // 4) Dedupe while preserving order
    return Array.from(new Set(keys));
  }

  private hit(
    map: Map<PermissionKey, Policy<R>[]>,
    keys: PermissionKey[],
    ctx: Ctx<R>
  ) {
    for (const k of keys) {
      for (const policy of map.get(k) ?? []) {
        if (policy(ctx)) return true;
      }
    }
    return false;
  }
}

const rbac = new RBAC<any>();

rbac.allowPermission('*', isAdmin);
registerGalleryPolicies(rbac);

export function can(permission: PermissionKey, ctx: Ctx<any>): boolean {
  return rbac.can(permission, ctx);
}

export default rbac;
